/**
 * 前端发布版本监测。
 *
 * 入口文件与静态分包都使用内容哈希，但移动浏览器可能恢复旧的
 * index.html / JS 页面。旧页面不能要求用户手动清缓存，因此启动后
 * 主动读取不可缓存的 release.json；发现线上版本变化时，用带版本查询
 * 参数的 URL 重新打开入口，强制浏览器重新获取最新 index.html。
 */

type ReleaseManifest = {
  release_id?: unknown;
};

const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_MARKER_PREFIX = 'prologue:release-reloaded:';

function currentReleaseId(): string {
  try {
    const value = (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_RELEASE_ID;
    return typeof value === 'string' ? value.trim() : '';
  } catch {
    return '';
  }
}

export function shouldReloadForRelease(current: string, remote: string): boolean {
  return Boolean(current && remote && current !== 'dev' && current !== remote);
}

function reloadForRelease(remoteRelease: string): void {
  if (typeof window === 'undefined') return;

  const marker = `${RELOAD_MARKER_PREFIX}${remoteRelease}`;
  try {
    if (window.sessionStorage.getItem(marker) === '1') return;
    window.sessionStorage.setItem(marker, '1');
  } catch {
    // 隐私模式下 sessionStorage 可能不可用，仍继续执行带查询参数的刷新。
  }

  const next = new URL(window.location.href);
  next.searchParams.set('__release', remoteRelease);
  window.location.replace(next.toString());
}

async function checkRelease(): Promise<void> {
  const current = currentReleaseId();
  if (!current || current === 'dev' || typeof fetch === 'undefined') return;

  try {
    const response = await fetch(`/release.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const manifest = await response.json() as ReleaseManifest;
    const remote = typeof manifest.release_id === 'string' ? manifest.release_id.trim() : '';
    if (shouldReloadForRelease(current, remote)) reloadForRelease(remote);
  } catch {
    // 发布探测失败不影响主应用运行；下一次启动/轮询继续探测。
  }
}

export function startReleaseMonitor(): void {
  if (typeof window === 'undefined') return;
  void checkRelease();
  window.setInterval(() => void checkRelease(), RELEASE_CHECK_INTERVAL_MS);
}
